import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Input, Button, Select, InputNumber, message, Row, Col, Divider, Card } from 'antd';
import {
  SaveOutlined,
  CloseOutlined,
  AppstoreOutlined,
  DollarOutlined,
  TagOutlined,
  FileTextOutlined,
  NumberOutlined,
  TeamOutlined
} from '@ant-design/icons';
import { createProduct, updateProduct, fetchProductFormData, getProduct } from '../../redux/actions/productActions';
import './Product.css';
import '../../styles/forms.css';

const { Option } = Select;

const ProductForm = () => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const {
    error,
    categories,
    units,
    taxSlabs,
    vendors,
    currentProduct,
    formDataLoading,
    formDataError
  } = useSelector((state) => state.products);

  useEffect(() => {
    // Fetch form data when component mounts
    dispatch(fetchProductFormData());
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      message.error(error);
    }
  }, [error]);

  useEffect(() => {
    if (formDataError) {
      message.error(formDataError);
    }
  }, [formDataError]);

  // Categories, units, and tax slabs are now fetched from the API
  // The static arrays are removed and replaced with dynamic data from the store

  useEffect(() => {
    if (id) {
      // If editing, fetch product details
      dispatch(getProduct(id));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (currentProduct && id) {
      // Pre-fill form with current product data
      form.setFieldsValue({
        category: currentProduct.category,
        productName: currentProduct.product_name,
        unit: currentProduct.unit,
        taxSlab: currentProduct.tax_slab,
        purchaseRate: currentProduct.purchase_rate,
        discount: currentProduct.discount,
        narration: currentProduct.narration,
        hsnSacCode: currentProduct.hsn_sac_code,
        vendorIds: (currentProduct.vendors || []).map(v => v.id)
      });
    }
  }, [currentProduct, id, form]);

  const onFinish = (values) => {
    const productData = {
      category: values.category,
      product_name: values.productName,
      unit: values.unit,
      tax_slab: values.taxSlab,
      purchase_rate: values.purchaseRate,
      discount: values.discount,
      narration: values.narration,
      hsn_sac_code: values.hsnSacCode,
      vendor_ids: values.vendorIds || []
    };

    if (id) {
      dispatch(updateProduct(id, productData));
    } else {
      dispatch(createProduct(productData));
    }
    navigate('/products');
  };

  return (
    <div className="page-container">
      <div className="page-content">
        <Card className="professional-form" style={{ maxWidth: 900, margin: '0 auto' }}>
          {/* Form Header */}
          <div className="form-modal-header" style={{ borderBottom: '2px solid #f0f0f0' }}>
            <div className="form-modal-icon">
              <AppstoreOutlined />
            </div>
            <div className="form-modal-title">
              <h3>{id ? 'Edit Product' : 'Add New Product'}</h3>
              <p>Manage your product inventory and pricing</p>
            </div>
          </div>

          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            style={{ marginTop: '1.5rem' }}
            requiredMark="optional"
          >
            {/* Basic Information */}
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="category"
                  label="Product Category"
                  rules={[{ required: true, message: 'Please select a category' }]}
                  tooltip="Select the category this product belongs to"
                >
                  <Select
                    placeholder="Select category"
                    loading={formDataLoading}
                    size="large"
                    suffixIcon={<TagOutlined />}
                  >
                    {categories.map(category => (
                      <Option key={category.id} value={category.value}>{category.label}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="unit"
                  label="Unit of Measurement"
                  rules={[{ required: true, message: 'Please select a unit' }]}
                  tooltip="Unit in which product will be measured"
                >
                  <Select
                    placeholder="Select unit"
                    loading={formDataLoading}
                    size="large"
                  >
                    {units.map(unit => (
                      <Option key={unit.value} value={unit.value}>{unit.label}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24}>
                <Form.Item
                  name="productName"
                  label="Product Name"
                  rules={[{ required: true, message: 'Please enter product name' }]}
                >
                  <Input
                    placeholder="Enter descriptive product name"
                    size="large"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24}>
                <Form.Item
                  name="hsnSacCode"
                  label="HSN / SAC Code"
                  rules={[{ required: true, message: 'Please enter HSN/SAC code' }]}
                  tooltip="Harmonized System of Nomenclature (HSN) or Service Accounting Code (SAC)"
                >
                  <Input
                    placeholder="Enter HSN/SAC code (e.g., 9963, 998314)"
                    size="large"
                    prefix={<NumberOutlined />}
                    maxLength={8}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Divider orientation="left" style={{ margin: '1.5rem 0 1rem' }}>
              <span style={{ fontSize: '0.85rem', color: '#666' }}>Pricing & Tax</span>
            </Divider>

            <Row gutter={16}>
              <Col xs={24} sm={8}>
                <Form.Item
                  name="purchaseRate"
                  label="Purchase Rate"
                  rules={[{ required: true, message: 'Please enter purchase rate' }]}
                  tooltip="Price per unit in INR"
                >
                  <InputNumber
                    prefix={<DollarOutlined />}
                    min={0}
                    style={{ width: '100%' }}
                    formatter={value => `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={value => value.replace(/₹\s?|(,*)/g, '')}
                    size="large"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item
                  name="discount"
                  label="Discount"
                  rules={[{ required: true, message: 'Please enter discount percentage' }]}
                  tooltip="Discount percentage (0-100)"
                >
                  <InputNumber
                    min={0}
                    max={100}
                    style={{ width: '100%' }}
                    formatter={value => `${value}%`}
                    parser={value => value.replace('%', '')}
                    size="large"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item
                  name="taxSlab"
                  label="Tax Slab (GST)"
                  rules={[{ required: true, message: 'Please select tax slab' }]}
                  tooltip="Applicable GST rate"
                >
                  <Select
                    placeholder="Select tax"
                    loading={formDataLoading}
                    size="large"
                  >
                    {taxSlabs.map(slab => (
                      <Option key={slab.value} value={slab.value}>{slab.label}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Divider style={{ margin: '1rem 0' }} />

            <Row gutter={16}>
              <Col xs={24}>
                <Form.Item
                  name="narration"
                  label="Product Description / Narration"
                  rules={[{ required: true, message: 'Please enter narration' }]}
                  tooltip="Additional details or specifications"
                >
                  <Input.TextArea
                    rows={4}
                    placeholder="Enter product description, specifications, or special notes"
                    showCount
                    maxLength={500}
                    size="large"
                    prefix={<FileTextOutlined />}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Divider orientation="left" style={{ margin: '1.5rem 0 1rem' }}>
              <span style={{ fontSize: '0.85rem', color: '#666' }}>Vendor Association</span>
            </Divider>

            <Row gutter={16}>
              <Col xs={24}>
                <Form.Item
                  name="vendorIds"
                  label="Linked Vendors"
                  tooltip="Select one or more vendors that supply this product. Optional."
                >
                  <Select
                    mode="multiple"
                    placeholder="Select vendors (optional)"
                    loading={formDataLoading}
                    size="large"
                    allowClear
                    optionFilterProp="label"
                    suffixIcon={<TeamOutlined />}
                    options={vendors.map(v => ({ value: v.value, label: v.label }))}
                  />
                </Form.Item>
              </Col>
            </Row>

            <div className="form-actions" style={{ marginTop: '2rem' }}>
              <Button onClick={() => navigate('/products')} size="large">
                <CloseOutlined />
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                size="large"
              >
                {id ? 'Update Product' : 'Create Product'}
              </Button>
            </div>
          </Form>
        </Card>
      </div>
    </div>
  );
};

export default ProductForm;